import React, { useEffect, useState } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc, updateDoc, addDoc, collection, getDocs, query, where, documentId, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useAuth } from "../../AuthContext";
import { findOurBarNear } from "../shared/ourBarNear";
import GamingEditor from "../charity/Gaming.jsx";
import PrintBarQr from "../shared/PrintBarQr";
import { AMENITY_GROUPS, EVENT_CATEGORY_OPTIONS, EVENT_SUBCATEGORY_OPTIONS } from "../shared/pillCatalog";



const SPECIAL_CATEGORY_OPTIONS = ["Daily Food", "Daily Drink", "Happy Hour", "Late Night", "Birthday"];

// The owner dashboard and the app store special categories as short values.
const SPECIAL_LABEL_TO_VALUE = {
  "Daily Food": "food",
  "Daily Drink": "drink",
  "Happy Hour": "happyhour",
  "Late Night": "latenight",
  "Birthday": "birthday",
};
const SPECIAL_VALUE_TO_LABEL = Object.fromEntries(Object.entries(SPECIAL_LABEL_TO_VALUE).map(([k, v]) => [v, k]));
const specialLabel = (cat) => SPECIAL_VALUE_TO_LABEL[cat] || cat || "";

// "15:00" -> "3 PM"
function shortTime(t) {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, "0")} ${period}` : `${h12} ${period}`;
}

// "15:00","18:00" -> "3–6 PM"
function timeRange(start, end) {
  const s = shortTime(start);
  const e = shortTime(end);
  if (!s) return "";
  if (!e) return `from ${s}`;
  const [sn, sp] = s.split(" ");
  const [en, ep] = e.split(" ");
  return sp === ep ? `${sn}–${en} ${ep}` : `${s}–${e}`;
}

const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ["Mon","Tue","Wed","Thu","Fri"] -> "Mon–Fri"
function compactDays(days) {
  const idx = [...new Set((days || []).map((d) => DAYS_SHORT.indexOf(d)).filter((i) => i >= 0))].sort((a, b) => a - b);
  if (idx.length === 0) return "";
  if (idx.length === 7) return "Every day";
  if (idx.length >= 3 && idx[idx.length - 1] - idx[0] === idx.length - 1) return `${DAYS_SHORT[idx[0]]}–${DAYS_SHORT[idx[idx.length - 1]]}`;
  return idx.map((i) => DAYS_SHORT[i]).join(", ");
}
const DAYS_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const JS_DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function dayOfWeekFromDate(dateStr) {
  if (!dateStr) return "";
  return JS_DAY_NAMES[new Date(`${dateStr}T00:00:00`).getDay()];
}

function findConflict(newEvent, allEvents) {
  return allEvents.find((ev) => {
    if (ev.time !== newEvent.time) return false;

    if (newEvent.recurrence === "one-time" && ev.recurrence === "one-time") {
      return ev.date === newEvent.date;
    }

    const newDay = newEvent.recurrence === "weekly" ? newEvent.dayOfWeek : dayOfWeekFromDate(newEvent.date);
    const existingDay = ev.recurrence === "weekly" ? ev.dayOfWeek : dayOfWeekFromDate(ev.date);
    return Boolean(newDay) && newDay === existingDay;
  });
}

function formatTime(time24) {
  if (!time24) return "";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

export default function AdminAddBar() {
  const { user, role } = useAuth();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const [selectedBar, setSelectedBar] = useState(null);
  const [phone, setPhone] = useState("");
  const [amenities, setAmenities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Specials (stored directly on the bar doc's `specials` array)
  const [specials, setSpecials] = useState([]);
  const [specialTitle, setSpecialTitle] = useState("");
  const [specialCategory, setSpecialCategory] = useState("Happy Hour");
  const [specialRecurrence, setSpecialRecurrence] = useState("daily");
  const [specialDays, setSpecialDays] = useState([]);
  const [specialDate, setSpecialDate] = useState("");
  const [specialDetails, setSpecialDetails] = useState("");
  const [specialStart, setSpecialStart] = useState("");
  const [specialEnd, setSpecialEnd] = useState("");
  const [editingSpecialId, setEditingSpecialId] = useState(null);

  // Events (stored in the top-level `events` collection)
  const [events, setEvents] = useState([]);
  const [eventTitle, setEventTitle] = useState("");
  const [eventCategory, setEventCategory] = useState("Gambling");
  const [eventSubcategories, setEventSubcategories] = useState([]);
  const [eventRecurrence, setEventRecurrence] = useState("one-time");
  const [eventDayOfWeek, setEventDayOfWeek] = useState("Monday");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [eventDetails, setEventDetails] = useState("");
  const [eventCharityName, setEventCharityName] = useState("");
  const [editingEventId, setEditingEventId] = useState(null);
  const [linkedCharityName, setLinkedCharityName] = useState("");

  const runSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;

    if (!window.google || !window.google.maps || !window.google.maps.places) {
      setError("Google Maps failed to load. Try refreshing the page.");
      return;
    }

    setSearching(true);
    setError("");

    const map = new window.google.maps.Map(document.createElement("div"));
    const service = new window.google.maps.places.PlacesService(map);

    service.textSearch(
      {
        query: `${search} bar OR pub OR tavern OR restaurant OR bingo OR casino`,
      },
      async (places, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !places) {
          setResults([]);
          setSearching(false);
          return;
        }

        const placeIds = places.map((p) => p.place_id);

        let existingDocs = {};
        if (placeIds.length > 0) {
          const q = query(collection(db, "bars"), where(documentId(), "in", placeIds.slice(0, 10)));
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            existingDocs[d.id] = d.data();
          });
        }

        // Bars from the national list have their own ids, not Google's: if one
        // of OURS is at that spot, edit ours instead of making a duplicate.
        const merged = await Promise.all(
          places.map(async (p) => {
            const lat = p.geometry?.location ? p.geometry.location.lat() : null;
            const lng = p.geometry?.location ? p.geometry.location.lng() : null;
            let id = p.place_id;
            let existing = existingDocs[p.place_id];
            if (!existing) {
              const ours = await findOurBarNear(p.name, lat, lng).catch(() => null);
              if (ours) {
                id = ours.id;
                existing = ours.data;
              }
            }
            return {
              place_id: id,
              name: existing?.name || p.name,
              address: existing?.address || p.formatted_address,
              lat,
              lng,
              alreadyExists: !!existing,
              alreadyClaimed: !!(existing?.ownerId || existing?.charityId),
            };
          })
        );

        setResults(merged);
        setSearching(false);
      }
    );
  };

  // Opened from another admin page with ?bar=<id> (Reports, Bars to call):
  // jump straight to editing that bar.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("bar");
    if (!id || role !== "admin") return;
    getDoc(doc(db, "bars", id)).then((snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      addBar({
        place_id: id,
        name: d.name || "",
        address: d.address || "",
        lat: d.lat ?? null,
        lng: d.lng ?? null,
        alreadyExists: true,
        alreadyClaimed: false,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const loadBarEvents = async (barId) => {
    const q = query(collection(db, "events"), where("barId", "==", barId));
    const snap = await getDocs(q);
    setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const addBar = async (place) => { if (place.alreadyClaimed && !window.confirm("This bar is already claimed by a real owner or charity. Continue editing on their behalf?")) { return; }
    setAddingId(place.place_id);
    setError("");

    try {
      const barRef = doc(db, "bars", place.place_id);
      const barSnap = await getDoc(barRef);

      if (!barSnap.exists()) {
        await setDoc(barRef, {
          name: place.name,
          address: place.address,
          lat: place.lat,
          lng: place.lng,
          state: "",
          zip: "",
          phone: "",
          photos: [],
          amenities: [],
          specials: [],
          birthdayDeals: [],
          ownerId: "",
          charityId: "",
        });
      }

      setSelectedBar({ id: place.place_id, name: place.name, address: place.address });
      setPhone("");
      setAmenities([]);
      setPhotos([]);
      setSpecials([]);
      setEditingSpecialId(null);
      setEditingEventId(null);
      setLinkedCharityName("");
      setEventCharityName("");

      if (barSnap.exists()) {
        const data = barSnap.data();
        setPhone(data.phone || "");
        setAmenities(data.amenities || []);
        setPhotos(data.photos || []);
        setSpecials(data.specials || []);
        setLinkedCharityName(data.gaming?.charityName || "");
        // Keep the gaming info so the Gaming Inventory box below can edit it.
        setSelectedBar((prev) => (prev ? { ...prev, gaming: data.gaming || null, charityId: data.charityId || "" } : prev));
      }

      await loadBarEvents(place.place_id);
    } catch (err) {
      console.error(err);
      setError("Failed to add this bar. Try again.");
    }

    setAddingId(null);
  };

  const toggleAmenity = (item) => {
    setAmenities((prev) =>
      prev.includes(item) ? prev.filter((a) => a !== item) : [...prev, item]
    );
  };

  const savePhoneAndAmenities = async () => {
    if (!selectedBar) return;
    await updateDoc(doc(db, "bars", selectedBar.id), { phone, amenities });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const uploadPhoto = async (file) => {
    if (!selectedBar || !file) return;
    setUploading(true);

    const fileRef = ref(storage, `bar-photos/${selectedBar.id}/${Date.now()}-${file.name}`);
    await uploadBytes(fileRef, file);
    const url = await getDownloadURL(fileRef);

    const updatedPhotos = [...photos, { url, path: fileRef.fullPath, id: Date.now() }];
    await updateDoc(doc(db, "bars", selectedBar.id), { photos: updatedPhotos });
    setPhotos(updatedPhotos);

    setUploading(false);
  };

  const removePhoto = async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await deleteObject(ref(storage, photo.path));
    } catch (err) {
      console.error("Storage delete failed, removing from list anyway", err);
    }
    const updatedPhotos = photos.filter((p) => p.id !== photo.id);
    await updateDoc(doc(db, "bars", selectedBar.id), { photos: updatedPhotos });
    setPhotos(updatedPhotos);
  };

  const toggleSpecialDay = (day) => {
    setSpecialDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const clearSpecialForm = () => {
    setSpecialTitle("");
    setSpecialDays([]);
    setSpecialDate("");
    setSpecialDetails("");
    setSpecialStart("");
    setSpecialEnd("");
    setEditingSpecialId(null);
  };

  // Adds a new special, or saves changes to the one being edited.
  const addSpecial = async () => {
    if (!selectedBar || !specialTitle.trim()) return;
    if (specialRecurrence === "days" && specialDays.length === 0) return;
    if (specialRecurrence === "one-time" && !specialDate) return;

    const fields = {
      title: specialTitle.trim(),
      category: SPECIAL_LABEL_TO_VALUE[specialCategory] || specialCategory,
      recurrence: specialRecurrence,
      days: specialRecurrence === "days" ? specialDays : [],
      date: specialRecurrence === "one-time" ? specialDate : "",
      details: specialDetails.trim(),
      startTime: specialStart,
      endTime: specialEnd,
    };

    let updatedSpecials;
    if (editingSpecialId !== null) {
      // Keep anything else on it (photo, source page) and mark it checked.
      updatedSpecials = specials.map((s) =>
        s.id === editingSpecialId ? { ...s, ...fields, verified: true } : s
      );
    } else {
      updatedSpecials = [...specials, { id: Date.now(), ...fields }];
    }
    await updateDoc(doc(db, "bars", selectedBar.id), { specials: updatedSpecials });
    setSpecials(updatedSpecials);
    clearSpecialForm();
  };

  const startEditSpecial = (s) => {
    setEditingSpecialId(s.id);
    setSpecialTitle(s.title || "");
    setSpecialCategory(specialLabel(s.category) || "Happy Hour");
    setSpecialRecurrence(s.recurrence || "daily");
    setSpecialDays(s.days || []);
    setSpecialDate(s.date || "");
    setSpecialDetails(s.details || "");
    setSpecialStart(s.startTime || "");
    setSpecialEnd(s.endTime || "");
    document.getElementById("admin-special-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const removeSpecial = async (specialId) => {
    if (!window.confirm("Remove this special?")) return;
    if (editingSpecialId === specialId) clearSpecialForm();
    const updatedSpecials = specials.filter((s) => s.id !== specialId);
    await updateDoc(doc(db, "bars", selectedBar.id), { specials: updatedSpecials });
    setSpecials(updatedSpecials);
  };

  const toggleEventSubcategory = (item) => {
    setEventSubcategories((prev) =>
      prev.includes(item) ? prev.filter((s) => s !== item) : [...prev, item]
    );
  };

  const handleEventCategoryChange = (newCategory) => {
    setEventCategory(newCategory);
    setEventSubcategories([]);
    setEventCharityName("");
  };

  const addEvent = async () => {
    if (!selectedBar || !eventTitle.trim() || !eventTime) return;
    if (eventRecurrence === "one-time" && !eventDate) return;

    const eventData = {
      barId: selectedBar.id,
      title: eventTitle.trim(),
      category: eventCategory,
      subcategories: eventSubcategories,
      recurrence: eventRecurrence,
      dayOfWeek: eventRecurrence === "weekly" ? eventDayOfWeek : "",
      date: eventRecurrence === "one-time" ? eventDate : "",
      time: eventTime,
      details: eventDetails.trim(),
      charityName: eventCategory === "Gambling" ? eventCharityName.trim() : "",
      createdBy: user?.uid || "",
      createdByRole: "admin",
      createdAt: Timestamp.now(),
    };

    const conflict = findConflict(eventData, events.filter((e) => e.id !== editingEventId));
    if (conflict) {
      const when = conflict.recurrence === "weekly" ? `every ${conflict.dayOfWeek}` : `on ${conflict.date}`;
      const postedBy =
        conflict.createdByRole === "owner"
          ? " (posted by the bar)"
          : conflict.createdByRole === "charity"
          ? " (posted by the charity)"
          : conflict.createdByRole === "admin"
          ? " (posted by admin)"
          : "";
      const proceed = window.confirm(
        `Heads up — there's already an event at this bar ${when} at ${formatTime(conflict.time)}: "${conflict.title}"${postedBy}. Add this one anyway?`
      );
      if (!proceed) return;
    }

    if (editingEventId) {
      // Save changes; keep who first created it, mark it checked.
      const { createdAt, createdBy, createdByRole, ...changes } = eventData;
      await updateDoc(doc(db, "events", editingEventId), { ...changes, verified: true });
      setEvents((prev) => prev.map((e) => (e.id === editingEventId ? { ...e, ...changes, verified: true } : e)));
    } else {
      const docRef = await addDoc(collection(db, "events"), eventData);
      setEvents((prev) => [...prev, { id: docRef.id, ...eventData }]);
    }
    clearEventForm();
  };

  const clearEventForm = () => {
    setEventTitle("");
    setEventSubcategories([]);
    setEventDate("");
    setEventTime("");
    setEventDetails("");
    setEventCharityName("");
    setEditingEventId(null);
  };

  const startEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setEventTitle(ev.title || "");
    setEventCategory(ev.category || "Gambling");
    setEventSubcategories(ev.subcategories || []);
    setEventRecurrence(ev.recurrence || "one-time");
    setEventDayOfWeek(ev.dayOfWeek || "Monday");
    setEventDate(ev.date || "");
    setEventTime(ev.time || "");
    setEventDetails(ev.details || "");
    setEventCharityName(ev.charityName || "");
    document.getElementById("admin-event-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const removeEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    const { deleteDoc } = await import("firebase/firestore");
    await deleteDoc(doc(db, "events", eventId));
    if (editingEventId === eventId) clearEventForm();
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    fontSize: "15px",
    boxSizing: "border-box",
  };
  const labelStyle = { display: "block", marginBottom: "8px", fontWeight: 600 };
  const sectionCardStyle = {
    background: "#0D0B1A",
    padding: "24px",
    borderRadius: "12px",
    maxWidth: "560px",
    marginTop: "24px",
  };

  if (role !== "admin") {
    return <p style={{ color: "#fff" }}>You don't have access to this page.</p>;
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "6px" }}>Add a Bar</h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        Search for a real bar and add it to Bar Fun Finder before the owner has claimed it or set up an
        account. It'll sit unclaimed, ready for them to take over later.
      </p>

      <form onSubmit={runSearch} style={{ display: "flex", gap: "10px", marginBottom: "24px", maxWidth: "600px" }}>
        <input
          type="text"
          placeholder="Search by name or city"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, padding: "14px", borderRadius: "8px", border: "none", fontSize: "18px" }}
        />
        <button
          type="submit"
          disabled={searching}
          style={{
            background: "linear-gradient(90deg, #6D28D9, #A855F7)",
            border: "none",
            padding: "12px 20px",
            borderRadius: "8px",
            color: "#fff",
            fontWeight: 600,
            fontSize: "16px",
            cursor: "pointer",
          }}
        >
          {searching ? "Searching..." : "Search"}
        </button>
      </form>

      {error && <p style={{ color: "#F472B6", marginBottom: "16px" }}>{error}</p>}

      {results.length > 0 && (
        <div style={{ marginBottom: "30px" }}>
          <h3 style={{ marginBottom: "10px" }}>Search Results</h3>
          {results.map((place) => (
            <div
              key={place.place_id}
              style={{
                background: "#0D0B1A",
                border: "1px solid rgba(255,255,255,0.08)",
                padding: "15px",
                borderRadius: "10px",
                marginBottom: "12px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontSize: "19px" }}>{place.name}</h4>
                <p style={{ margin: "4px 0 0", color: "#B8B0C8", fontSize: "15px" }}>{place.address}</p>
                {place.alreadyClaimed && (
                  <p style={{ margin: "4px 0 0", color: "#F472B6", fontSize: "16px" }}>Already claimed by an owner/charity</p>
                )}
                {place.alreadyExists && !place.alreadyClaimed && (
                  <p style={{ margin: "4px 0 0", color: "#8A84A0", fontSize: "16px" }}>Already added, still unclaimed</p>
                )}
              </div>
              <button
                onClick={() => addBar(place)}
                disabled={addingId === place.place_id}
                style={{
                  background: place.alreadyClaimed ? "linear-gradient(90deg, #B45309, #F59E0B)" : "linear-gradient(90deg, #6D28D9, #A855F7)",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: "8px",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "15px",
                  cursor: "pointer",
                }}
              >
                {addingId === place.place_id ? "Adding..." : place.alreadyClaimed ? "Edit (Claimed by Owner)" : place.alreadyExists ? "Edit This Bar" : "Add This Bar"}
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedBar && (
        <>
          <div style={sectionCardStyle}>
            <h3 style={{ marginBottom: "4px" }}>{selectedBar.name}</h3>
            <p style={{ marginBottom: "12px", color: "#B8B0C8", fontSize: "16px" }}>{selectedBar.address}</p>
            <div style={{ marginBottom: "20px" }}>
              <PrintBarQr barId={selectedBar.id} barName={selectedBar.name} />
            </div>

            <label style={labelStyle}>Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              style={{ ...inputStyle, marginBottom: "20px" }}
            />

            <label style={labelStyle}>Amenities</label>
            {Object.entries(AMENITY_GROUPS).map(([groupName, items]) => (
              <div key={groupName} style={{ marginBottom: "14px" }}>
                <p style={{ fontSize: "15px", color: "#8A84A0", margin: "0 0 8px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {groupName}
                </p>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {items.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleAmenity(item)}
                      style={{
                        padding: "8px 14px",
                        borderRadius: "20px",
                        border: amenities.includes(item) ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                        background: amenities.includes(item) ? "rgba(168,85,247,0.15)" : "transparent",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <button
              onClick={savePhoneAndAmenities}
              style={{
                background: saved ? "#4ADE80" : "linear-gradient(90deg, #6D28D9, #A855F7)",
                border: "none",
                padding: "10px 18px",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "24px",
              }}
            >
              {saved ? "✓ Saved" : "Save Phone & Amenities"}
            </button>

            <label style={labelStyle}>Photos</label>
            <label
              style={{
                display: "block",
                border: "1px dashed rgba(255,255,255,0.3)",
                borderRadius: "8px",
                padding: "20px",
                textAlign: "center",
                cursor: "pointer",
                marginBottom: "18px",
                color: "#B8B0C8",
              }}
            >
              {uploading ? "Uploading..." : "+ Add a photo"}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={uploading}
                onChange={(e) => uploadPhoto(e.target.files[0])}
              />
            </label>

            {photos.length === 0 ? (
              <p style={{ color: "#B8B0C8" }}>No photos uploaded yet.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                {photos.map((photo) => (
                  <div key={photo.id} style={{ position: "relative" }}>
                    <img
                      src={photo.url}
                      alt="Bar"
                      style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "8px", display: "block" }}
                    />
                    <button
                      onClick={() => removePhoto(photo)}
                      style={{
                        position: "absolute",
                        top: "4px",
                        right: "4px",
                        background: "rgba(0,0,0,0.6)",
                        border: "none",
                        borderRadius: "6px",
                        color: "#fff",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        fontSize: "14px",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div id="admin-special-form" style={sectionCardStyle}>
            <h3 style={{ marginBottom: "10px" }}>{editingSpecialId !== null ? "Edit Special" : "Add a Special"}</h3>

            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={specialTitle}
              onChange={(e) => setSpecialTitle(e.target.value)}
              placeholder="$2 Taps"
              style={{ ...inputStyle, marginBottom: "16px" }}
            />

            <label style={labelStyle}>Category</label>
            <select
              value={specialCategory}
              onChange={(e) => setSpecialCategory(e.target.value)}
              style={{ ...inputStyle, marginBottom: "16px" }}
            >
              {SPECIAL_CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <label style={labelStyle}>When does this run?</label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              {["daily", "days", "one-time"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setSpecialRecurrence(r)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    borderRadius: "8px",
                    border: specialRecurrence === r ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                    background: specialRecurrence === r ? "rgba(168,85,247,0.15)" : "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  {r === "daily" ? "Every day" : r === "days" ? "Specific days" : "One-time"}
                </button>
              ))}
            </div>

            {specialRecurrence === "days" && (
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "16px" }}>
                {DAYS_SHORT.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleSpecialDay(day)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "20px",
                      border: specialDays.includes(day) ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                      background: specialDays.includes(day) ? "rgba(168,85,247,0.15)" : "transparent",
                      color: "#fff",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>
            )}

            {specialRecurrence === "one-time" && (
              <input
                type="date"
                value={specialDate}
                onChange={(e) => setSpecialDate(e.target.value)}
                style={{ ...inputStyle, marginBottom: "16px" }}
              />
            )}

            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle} htmlFor="special-start">Starts (optional)</label>
                <input id="special-start" type="time" value={specialStart} onChange={(e) => setSpecialStart(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle} htmlFor="special-end">Ends (optional)</label>
                <input id="special-end" type="time" value={specialEnd} onChange={(e) => setSpecialEnd(e.target.value)} style={inputStyle} />
              </div>
            </div>

            <label style={labelStyle}>Details (optional)</label>
            <textarea
              value={specialDetails}
              onChange={(e) => setSpecialDetails(e.target.value)}
              placeholder="Any extra details customers should see"
              style={{ ...inputStyle, marginBottom: "16px", height: "70px" }}
            />

            <button
              onClick={addSpecial}
              style={{
                background: "linear-gradient(90deg, #6D28D9, #A855F7)",
                border: "none",
                padding: "10px 18px",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "20px",
              }}
            >
              {editingSpecialId !== null ? "Save Changes" : "Add Special"}
            </button>
            {editingSpecialId !== null && (
              <button
                type="button"
                onClick={clearSpecialForm}
                style={{ marginLeft: "10px", background: "transparent", border: "1px solid rgba(255,255,255,0.3)", padding: "10px 18px", borderRadius: "8px", color: "#fff", cursor: "pointer" }}
              >
                Cancel
              </button>
            )}

            {specials.length > 0 && (
              <>
                <h4 style={{ marginBottom: "8px" }}>Current Specials</h4>
                {specials.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      background: "#16121F",
                      padding: "12px",
                      borderRadius: "8px",
                      marginBottom: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{s.title}</div>
                      <div style={{ fontSize: "15px", color: "#B8B0C8" }}>
                        {specialLabel(s.category)} ·{" "}
                        {s.recurrence === "daily" ? "Every day" : s.recurrence === "days" ? compactDays(s.days) : s.date}
                        {timeRange(s.startTime, s.endTime) ? ` · ${timeRange(s.startTime, s.endTime)}` : ""}
                      </div>
                      {s.details && <div style={{ fontSize: "15px", color: "#D9CCFF", marginTop: "2px" }}>{s.details}</div>}
                      {s.source === "web" && !s.verified && (
                        <div style={{ fontSize: "13px", color: "#8A84A0", marginTop: "2px" }}>
                          From their website{s.sourceUrl ? <> · <a href={s.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#B9A6FF" }}>source</a></> : null}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                      <button
                        onClick={() => startEditSpecial(s)}
                        style={{ background: "transparent", border: "none", color: "#B9A6FF", cursor: "pointer", fontSize: "16px", minHeight: "44px", padding: "0 8px" }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeSpecial(s.id)}
                        style={{ background: "transparent", border: "none", color: "#F472B6", cursor: "pointer", fontSize: "16px", minHeight: "44px", padding: "0 8px" }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          <div style={sectionCardStyle}>
            <GamingEditor
              key={`${selectedBar.id}-${selectedBar.gaming ? "g" : "n"}`}
              mode="admin"
              adminBar={selectedBar}
            />
          </div>

          <div id="admin-event-form" style={sectionCardStyle}>
            <h3 style={{ marginBottom: "10px" }}>{editingEventId ? "Edit Event" : "Add an Event"}</h3>

            <label style={labelStyle}>Category</label>
            <select
              value={eventCategory}
              onChange={(e) => handleEventCategoryChange(e.target.value)}
              style={{ ...inputStyle, marginBottom: "12px" }}
            >
              {EVENT_CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <label style={labelStyle}>What kind of {eventCategory.toLowerCase()}? (Check all that apply)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
              {(EVENT_SUBCATEGORY_OPTIONS[eventCategory] || []).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => toggleEventSubcategory(item)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "20px",
                    border: eventSubcategories.includes(item) ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                    background: eventSubcategories.includes(item) ? "rgba(168,85,247,0.15)" : "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "16px",
                  }}
                >
                  {item}
                </button>
              ))}
            </div>

            {eventCategory === "Gambling" && (
              <>
                <label style={labelStyle}>Who's running this? (shown to customers for transparency)</label>
                {linkedCharityName && eventCharityName !== linkedCharityName && (
                  <button
                    type="button"
                    onClick={() => setEventCharityName(linkedCharityName)}
                    style={{
                      display: "block",
                      marginBottom: "10px",
                      padding: "8px 14px",
                      borderRadius: "8px",
                      border: "1px solid rgba(168,85,247,0.5)",
                      background: "rgba(168,85,247,0.15)",
                      color: "#fff",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    Use linked charity: {linkedCharityName}
                  </button>
                )}
                <input
                  type="text"
                  value={eventCharityName}
                  onChange={(e) => setEventCharityName(e.target.value)}
                  placeholder="e.g. American Legion Post 123 — or the bar's name if it's not run by a charity"
                  style={{ ...inputStyle, marginBottom: "16px" }}
                />
              </>
            )}

            <label style={labelStyle}>Event Title</label>
            <input
              type="text"
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
              placeholder="Bingo, $1,000 coverall"
              style={{ ...inputStyle, marginBottom: "16px" }}
            />

            <label style={labelStyle}>Does this repeat?</label>
            <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
              <button
                type="button"
                onClick={() => setEventRecurrence("one-time")}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: eventRecurrence === "one-time" ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                  background: eventRecurrence === "one-time" ? "rgba(168,85,247,0.15)" : "transparent",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                One-time event
              </button>
              <button
                type="button"
                onClick={() => setEventRecurrence("weekly")}
                style={{
                  flex: 1,
                  padding: "10px",
                  borderRadius: "8px",
                  border: eventRecurrence === "weekly" ? "2px solid #A855F7" : "1px solid rgba(255,255,255,0.2)",
                  background: eventRecurrence === "weekly" ? "rgba(168,85,247,0.15)" : "transparent",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Every week
              </button>
            </div>

            {eventRecurrence === "weekly" ? (
              <select
                value={eventDayOfWeek}
                onChange={(e) => setEventDayOfWeek(e.target.value)}
                style={{ ...inputStyle, marginBottom: "16px" }}
              >
                {DAYS_FULL.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                style={{ ...inputStyle, marginBottom: "16px" }}
              />
            )}

            <label style={labelStyle}>Time</label>
            <input
              type="time"
              value={eventTime}
              onChange={(e) => setEventTime(e.target.value)}
              style={{ ...inputStyle, marginBottom: "16px" }}
            />

            <label style={labelStyle}>Details (optional)</label>
            <textarea
              value={eventDetails}
              onChange={(e) => setEventDetails(e.target.value)}
              placeholder="Any extra details customers should see"
              style={{ ...inputStyle, marginBottom: "16px", height: "70px" }}
            />

            <button
              onClick={addEvent}
              style={{
                background: "linear-gradient(90deg, #6D28D9, #A855F7)",
                border: "none",
                padding: "10px 18px",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: "20px",
              }}
            >
              {editingEventId ? "Save Changes" : "Publish Event"}
            </button>
            {editingEventId && (
              <button
                type="button"
                onClick={clearEventForm}
                style={{ marginLeft: "10px", background: "transparent", border: "1px solid rgba(255,255,255,0.3)", padding: "10px 18px", borderRadius: "8px", color: "#fff", cursor: "pointer" }}
              >
                Cancel
              </button>
            )}

            {events.length > 0 && (
              <>
                <h4 style={{ marginBottom: "8px" }}>Current Events</h4>
                {events.map((ev) => (
                  <div
                    key={ev.id}
                    style={{
                      background: "#16121F",
                      padding: "12px",
                      borderRadius: "8px",
                      marginBottom: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{ev.title}</div>
                      <div style={{ fontSize: "15px", color: "#B8B0C8" }}>
                        {ev.subcategories && ev.subcategories.length > 0 ? ev.subcategories.join(", ") : ev.category} ·{" "}
                        {ev.recurrence === "weekly" ? `Every ${ev.dayOfWeek}` : ev.recurrence === "daily" ? "Every day" : ev.date} at {shortTime(ev.time) || ev.time}
                        {ev.charityName ? ` · Run by ${ev.charityName}` : ""}
                      </div>
                      {ev.details && <div style={{ fontSize: "15px", color: "#D9CCFF", marginTop: "2px" }}>{ev.details}</div>}
                      {ev.source === "web" && !ev.verified && (
                        <div style={{ fontSize: "13px", color: "#8A84A0", marginTop: "2px" }}>
                          From their website{ev.sourceUrl ? <> · <a href={ev.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#B9A6FF" }}>source</a></> : null}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                      <button
                        onClick={() => startEditEvent(ev)}
                        style={{ background: "transparent", border: "none", color: "#B9A6FF", cursor: "pointer", fontSize: "16px", minHeight: "44px", padding: "0 8px" }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => removeEvent(ev.id)}
                        style={{ background: "transparent", border: "none", color: "#F472B6", cursor: "pointer", fontSize: "16px", minHeight: "44px", padding: "0 8px" }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
